/**
 * Local-storage cart cache.
 *
 * Stores the Shopify cart object so returning users in the Quiz Abandoner
 * Menu test variation can render the menu immediately on entry instead of
 * waiting for the API to resolve.
 *
 * Cookie's expiration is the effective TTL
 *
 * `createCartCache` lets another flow (e.g. Flat Rate) keep its own cache
 * keyed to its own cart cookie and localStorage prefix. The named exports
 * below are /onboarding's instance.
 */

import Cookies from 'js-cookie';

const CART_COOKIE_NAME = 'shopifyCartId';
const STORAGE_KEY_PREFIX = 'cart:';

const isBrowser = () => typeof window !== 'undefined' && !!window.localStorage;

/**
 * @param {object} config
 * @param {string} config.cartCookieName cookie holding the flow's cart id
 * @param {string} config.storageKeyPrefix localStorage key prefix; must be
 *   unique per flow, since stale-entry cleanup clears every key under it
 */
export const createCartCache = ({ cartCookieName, storageKeyPrefix }) => {
  const getStorageKey = cartId => `${storageKeyPrefix}${cartId}`;

  const getCartIdFromCookie = () => {
    try {
      return Cookies.get(cartCookieName) ?? null;
    } catch {
      return null;
    }
  };

  /**
   * Clean up localStorage entries with `${storageKeyPrefix}*` prefix
   *
   * exceptCartId: optional prop used when writing the current
   * cart's marker so we don't delete then rewrite the same key
   */
  const clearStaleCartCaches = (exceptCartId = null) => {
    if (!isBrowser()) return;
    try {
      const exceptKey = exceptCartId ? getStorageKey(exceptCartId) : null;
      const keysToRemove = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(storageKeyPrefix) && key !== exceptKey) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => window.localStorage.removeItem(key));
    } catch {}
  };

  const readCachedCart = () => {
    if (!isBrowser()) return null;

    const cartId = getCartIdFromCookie();
    if (!cartId) return null;

    let raw;
    try {
      raw = window.localStorage.getItem(getStorageKey(cartId));
    } catch {
      return null;
    }
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      try {
        window.localStorage.removeItem(getStorageKey(cartId));
      } catch {}
      return null;
    }
  };

  const writeCachedCart = cart => {
    if (!isBrowser() || !cart) return;

    const cartId = cart.cart_id || getCartIdFromCookie();
    if (!cartId) return;

    const storageKey = getStorageKey(cartId);

    try {
      if (!window.localStorage.getItem(storageKey)) {
        clearStaleCartCaches(cartId);
      }
      window.localStorage.setItem(storageKey, JSON.stringify(cart));
    } catch {}
  };

  const clearCachedCart = () => clearStaleCartCaches();

  return { readCachedCart, writeCachedCart, clearCachedCart };
};

export const { readCachedCart, writeCachedCart, clearCachedCart } =
  createCartCache({
    cartCookieName: CART_COOKIE_NAME,
    storageKeyPrefix: STORAGE_KEY_PREFIX,
  });

export const CART_CACHE_STORAGE_KEY_PREFIX = STORAGE_KEY_PREFIX;
export const CART_COOKIE_KEY = CART_COOKIE_NAME;
